/**
 * Who may manage whom on the Team page, and which roles a member may give.
 *
 * Mirrors the rule the API enforces: an owner manages everyone and gives every
 * role; anyone else manages only members whose role is below their own and
 * gives only roles below their own. The only owner of an organisation cannot
 * be removed, given another role or deactivated.
 *
 * The API stays the authority. Where the loaded data cannot tell (a custom
 * role without a capability list), the control stays offered and the API's
 * answer decides.
 */

/** System roles, highest first. */
export const SYSTEM_ROLE_ORDER = [
  'owner',
  'manager',
  'team_lead',
  'analyst',
  'read_only',
] as const;

const RANK: Record<string, number> = {
  owner: 5,
  manager: 4,
  team_lead: 3,
  analyst: 2,
  read_only: 1,
};

/** The part of a loaded role this rule needs. */
export interface RankRole {
  key: string;
  capabilities?: string[] | null;
}

/** The part of a member row this rule needs. */
export interface RankMember {
  id: string;
  role: string;
  active?: boolean;
  invitation_accepted_at?: string | null;
  /** Set when the member is invited; null on a row that never was. */
  invitation_token_expires_at?: string | null;
}

export interface RankCaller {
  role: string | null | undefined;
  /** The signed-in member's own employee id, when known. */
  id?: string | null;
}

const capabilitiesOf = (key: string, roles: RankRole[]): string[] | null => {
  const found = roles.find((r) => r.key === key);
  return found && Array.isArray(found.capabilities) ? found.capabilities : null;
};

/**
 * Is `role` below `callerRole`? An owner is above every role. Between two
 * system roles the fixed order decides. A caller on a custom role never
 * outranks a system role. A custom role is below the caller only when every
 * capability it carries is one the caller's role has; when the loaded roles
 * cannot tell, the answer is yes.
 */
export function isRoleBelow(
  callerRole: string | null | undefined,
  role: string,
  roles: RankRole[] = []
): boolean {
  if (!callerRole) return false;
  if (callerRole === 'owner') return true;
  if (role === 'owner') return false;

  const callerRank = RANK[callerRole];
  const rank = RANK[role];
  if (callerRank !== undefined && rank !== undefined) return rank < callerRank;
  if (callerRank === undefined && rank !== undefined) return false;

  const callerCaps = capabilitiesOf(callerRole, roles);
  const caps = capabilitiesOf(role, roles);
  if (!callerCaps || !caps) return true;
  return caps.every((c) => callerCaps.includes(c));
}

/**
 * May the caller edit, delete, re-invite or change the brands of this member?
 * An owner may, for everyone. Everyone may for their own row (the page decides
 * with its capability gates what that row offers). Otherwise only for a member
 * whose role is below the caller's.
 */
export function canManageMember(
  caller: RankCaller,
  member: Pick<RankMember, 'id' | 'role'>,
  roles: RankRole[] = []
): boolean {
  if (!caller.role) return false;
  if (caller.role === 'owner') return true;
  if (caller.id && caller.id === member.id) return true;
  return isRoleBelow(caller.role, member.role, roles);
}

/** The roles, out of `options`, the caller may give to a member. */
export function assignableRoles<T extends { key: string }>(
  callerRole: string | null | undefined,
  options: T[],
  roles: RankRole[] = []
): T[] {
  return options.filter((o) => isRoleBelow(callerRole, o.key, roles));
}

/**
 * A row that is only an invitation so far: not accepted, with an invitation
 * outstanding. The account holder's own row was never invited and has neither
 * date, so it is not pending.
 */
export const isPendingInvitation = (
  m: Pick<RankMember, 'invitation_accepted_at' | 'invitation_token_expires_at'>
): boolean =>
  m.invitation_accepted_at == null && m.invitation_token_expires_at != null;

const isStandingOwner = (m: RankMember): boolean =>
  m.role === 'owner' && m.active !== false && !isPendingInvitation(m);

/**
 * Is this member the organisation's only owner? Counts active owner rows; a
 * pending owner invitation is not an owner yet.
 */
export function isOnlyOwner(
  member: RankMember,
  members: RankMember[]
): boolean {
  if (!isStandingOwner(member)) return false;
  return members.filter(isStandingOwner).length === 1;
}

/**
 * The sentence to show for a refused team request: the API's `reason` when it
 * gave one, else its message, else the fallback.
 */
export function teamErrorText(err: unknown, fallback: string): string {
  const e = (err ?? {}) as { reason?: unknown; message?: unknown };
  if (typeof e.reason === 'string' && e.reason.trim()) return e.reason;
  if (typeof e.message === 'string' && e.message.trim()) return e.message;
  return fallback;
}

/**
 * The numbers on the Team page tiles. A member is an active row that is not an
 * outstanding invitation (the account holder's own row counts); an approver is
 * a member whose role may approve. Outstanding invitations are counted apart.
 */
export function teamCounts(
  rows: Pick<
    RankMember,
    'role' | 'active' | 'invitation_accepted_at' | 'invitation_token_expires_at'
  >[],
  mayApprove: (role: string) => boolean
): { members: number; approvers: number; pending: number } {
  const members = rows.filter(
    (m) => m.active !== false && !isPendingInvitation(m)
  );
  return {
    members: members.length,
    approvers: members.filter((m) => mayApprove(m.role)).length,
    pending: rows.filter(isPendingInvitation).length,
  };
}
